import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { InvitationAcceptRequest } from '../request-models/invitation-accept-request.model';
import { InvitationAcceptResponse } from '../response-models/invitation-accept-response.model';
import { InvitationLookupResponse } from '../response-models/invitation-lookup-response.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';

/**
 * Public invitation lookup and acceptance by emailed token: `/auth/invitations/:token[/accept]`
 * (hub `auth-unified-invitation-controller.ts`). The token is always sent as one path segment.
 * Failures reject with an `ApiError` whose `code` is lowercase on these routes (`invalid_token`,
 * `expired`, `cancelled`, `already_accepted`, `email_mismatch`, `invalid_password`, `email_exists`).
 */
@Injectable({
  providedIn: 'root'
})
export class AuthInvitationService extends ApiService {
  constructor() {
    super('auth/invitations');
  }

  /** Look up an invitation and the acceptance flow the invitee must follow. */
  async getInvitation(token: string): Promise<InvitationLookupResponse> {
    return firstValueFrom(this.request('GET', { id: token }, toInvitationLookup));
  }

  /** Accept an invitation, following the flow from `getInvitation`. */
  async acceptInvitation(token: string, body: InvitationAcceptRequest): Promise<InvitationAcceptResponse> {
    return firstValueFrom(this.request('POST', { id: token, path: 'accept' }, toInvitationAccept, body));
  }
}

/** The lookup is display data from a public endpoint; the page reads it as-is. */
function toInvitationLookup(data: unknown): InvitationLookupResponse {
  if (!isRecord(data) || !isRecord(data['invitation']) || !isRecord(data['flow'])) {
    throw new Error('Invalid invitation response');
  }
  return data as unknown as InvitationLookupResponse;
}

function toInvitationAccept(data: unknown): InvitationAcceptResponse {
  const source = isRecord(data) ? data : {};
  return {
    flow: nullableString(source['flow']) as InvitationAcceptResponse['flow'],
    userId: nullableString(source['userId']) ?? undefined,
    email: nullableString(source['email']) ?? undefined,
    customToken: nullableString(source['customToken']) ?? undefined,
    organizationId: nullableString(source['organizationId']) ?? undefined,
  };
}
